import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ContextService } from 'src/app/view/user-context/services/context.service';
import { ContextType } from 'src/app/view/user-context/services/models/context.data';
import { BrowserNtf } from './browser-ntf.constants';

@Injectable({ providedIn: 'root' })
export class BrowserNtfNavigationService {
  constructor(
    private router: Router,
    private zone: NgZone,
    private ctxSrv: ContextService
  ) {}

  /**
   * Navigates to URL received from notification payload.
   * If payload includes estate context (query param or explicit value),
   * context is applied before route navigation and helper query is removed.
   * @param {string | null | undefined} url
   * @param {string | undefined}        fallbackEstateId
   * @return {void}
   */
  public navigateToNotificationUrl(url?: string | null, fallbackEstateId?: string): void {
    if (!url) return;

    try {
      const parsedUrl = new URL(url, window.location.origin);
      if (parsedUrl.origin !== window.location.origin) {
        window.location.assign(parsedUrl.href);
        return;
      }

      const estateId = parsedUrl.searchParams.get(BrowserNtf.ESTATE_QUERY_KEY) ?? fallbackEstateId ?? undefined;
      const companyId = parsedUrl.searchParams.get(BrowserNtf.COMPANY_QUERY_KEY) ?? undefined;
      if (estateId) {
        this.applyEstateContext(estateId);
      } else if (companyId) {
        this.applyCompanyContext(companyId);
      }
      parsedUrl.searchParams.delete(BrowserNtf.ESTATE_QUERY_KEY);
      parsedUrl.searchParams.delete(BrowserNtf.COMPANY_QUERY_KEY);

      const search = parsedUrl.searchParams.toString();
      const path = `${parsedUrl.pathname}${search ? `?${search}` : ''}${parsedUrl.hash}`;
      this.zone.run(() => {
        this.router.navigateByUrl(path);
      });
      window.focus();
    } catch {
      window.location.assign(url);
    }
  }

  /**
   * Reads notification context query parameters from current browser URL on app startup.
   * When a parameter exists, it applies the context and removes helper parameters without reloading the page.
   * @return {void}
   */
  public consumeEstateContextFromCurrentUrl(): void {
    if (typeof window === 'undefined') return;

    try {
      const url = new URL(window.location.href);
      const estateId = url.searchParams.get(BrowserNtf.ESTATE_QUERY_KEY);
      const companyId = url.searchParams.get(BrowserNtf.COMPANY_QUERY_KEY);
      if (!estateId && !companyId) return;

      if (estateId) this.applyEstateContext(estateId);
      else if (companyId) this.applyCompanyContext(companyId);
      url.searchParams.delete(BrowserNtf.ESTATE_QUERY_KEY);
      url.searchParams.delete(BrowserNtf.COMPANY_QUERY_KEY);
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    } catch {
      // URL parsing failed; no context to apply.
    }
  }

  /**
   * Sets CRM context to estate and selects estate id used by notification.
   * @param {string} estateId
   * @return {void}
   */
  private applyEstateContext(estateId: string): void {
    if (!estateId) return;
    this.ctxSrv.setContext(ContextType.ESTATE);
    this.ctxSrv.setEstate(estateId);
  }

  /** Applies company context encoded in an offer notification link. */
  private applyCompanyContext(companyId: string): void {
    if (!companyId) return;
    this.ctxSrv.setContext(ContextType.COMPANY);
    this.ctxSrv.setCompany(companyId);
  }
}

